import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Phone, X, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface SOSChatProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  onSend: (content: string) => void;
  onResolve: () => void;
}

export function SOSChat({ messages, isStreaming, onSend, onResolve }: SOSChatProps) {
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;
    onSend(input.trim());
    setInput("");
  };

  // Quick response buttons
  const quickResponses = [
    "Sí, estoy bien",
    "Hay un herido",
    "No respira",
    "Hay sangre",
    "Ya llamé al 112",
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: "100%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "100%" }}
      className="fixed inset-0 z-[90] bg-background flex flex-col"
    >
      {/* Header */}
      <div className="bg-destructive/10 border-b border-destructive/30 px-4 py-3">
        <div className="flex items-center justify-between max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive animate-pulse" />
            <div>
              <h2 className="font-mono font-bold text-foreground text-sm">
                EMERGENCIA ACTIVA
              </h2>
              <p className="text-[10px] text-muted-foreground font-mono">
                Asistente de primeros auxilios
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="tel:112"
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs font-bold"
            >
              <Phone className="w-3.5 h-3.5" />
              112
            </a>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 max-w-md md:max-w-2xl lg:max-w-4xl mx-auto w-full">
        <AnimatePresence>
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-3 flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-destructive/10 border border-destructive/20 text-foreground rounded-bl-sm"
                }`}
              >
                {msg.role === "assistant" && (
                  <p className="text-[10px] font-mono text-destructive mb-1 font-bold">
                    🚑 ASISTENTE SOS
                  </p>
                )}
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
          <div className="flex justify-start mb-3">
            <div className="bg-destructive/10 border border-destructive/20 rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-destructive rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-destructive rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-destructive rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick responses */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2 max-w-md md:max-w-2xl lg:max-w-4xl mx-auto w-full">
          <div className="flex flex-wrap gap-2">
            {quickResponses.map((qr) => (
              <button
                key={qr}
                onClick={() => onSend(qr)}
                disabled={isStreaming}
                className="px-3 py-1.5 rounded-full border border-border bg-secondary/50 text-xs font-mono text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
              >
                {qr}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border px-4 py-3 bg-card/95 backdrop-blur-sm">
        <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto flex gap-2">
          <form onSubmit={handleSubmit} className="flex-1 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe la situación..."
              className="flex-1 input-paddock"
              disabled={isStreaming}
            />
            <Button
              type="submit"
              disabled={!input.trim() || isStreaming}
              size="icon"
              className="bg-destructive hover:bg-destructive/90"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <Button
            onClick={onResolve}
            variant="outline"
            size="icon"
            className="border-success/50 text-success hover:bg-success/10"
            title="Estoy bien - resolver alerta"
          >
            <CheckCircle className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
